import "./cart.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
import { Backdrop } from "@ts/utils";

type CartHook = () => void | Promise<void>;
type CartOptions = {
    onClick?: CartHook;
    onOpen?: CartHook;
    onClose?: CartHook;
    panelSelector?: string;
};

export class Cart {
    private readonly id: string;
    private readonly trigger: HTMLButtonElement | HTMLAnchorElement;
    private static registry = new ComponentRegistry<Cart>('cart');
    private panel: HTMLElement | null = null;
    private backdrop: Backdrop | null = null;
    private escapeKeyBound = false;

    private constructor(
        trigger: HTMLButtonElement | HTMLAnchorElement,
        private options: CartOptions
    ) {
        this.id = Cart.registry.register(trigger, this);
        this.trigger = trigger;
        this.options.panelSelector = options.panelSelector ?? 'cart-panel';
        this.panel = document.querySelector(this.options.panelSelector);
        this.setClickEvent();
        this.bindEscapeKey();
    }

    private setClickEvent(): void {
        this.trigger.addEventListener('click', async (e) => {
            e.preventDefault();

            if (typeof this.options.onClick === 'function') {
                await this.options.onClick();
            }

            this.toggle();
        });
    }

    private toggle(open?: boolean): void {
        const isOpen = open ?? !this.panel?.classList.contains('cart-panel--open');

        this.panel?.classList.toggle('cart-panel--open', isOpen);
        this.trigger.setAttribute('aria-expanded', String(isOpen));
        this.panel?.setAttribute('aria-hidden', String(!isOpen));

        if (isOpen) {
            this.backdrop = new Backdrop(() => this.toggle(false));
            this.backdrop.show();
            if (typeof this.options.onOpen === 'function') {
                this.options.onOpen();
            }
        } else {
            this.backdrop?.hide();
            this.backdrop = null;
            if (typeof this.options.onClose === 'function') {
                this.options.onClose();
            }
        }
    }

    private bindEscapeKey(): void {
        if (this.escapeKeyBound) return;

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.panel?.classList.contains('cart-panel--open')) {
                this.toggle(false);
            }
        });

        this.escapeKeyBound = true;
    }

    public open(): void {
        this.toggle(true);
    }

    public close(): void {
        this.toggle(false);
    }

    public static create(
        target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
        options: CartOptions = {}
    ): Cart[] | null {
        const triggers = ComponentRegistry.processTargets<Cart, HTMLElement>(
            Cart.registry,
            target,
            [HTMLButtonElement, HTMLAnchorElement]
        ) as (HTMLButtonElement | HTMLAnchorElement)[];

        if (!triggers.length) return null;

        return triggers.map(trigger => new Cart(trigger, options));
    }

    public static getCart(id: string): Cart | null {
        return Cart.registry.get(id) ?? null;
    }

    public static closeAll(): void {
        for (const id of Cart.registry.getIds()) {
            const instance = Cart.registry.get(id);
            instance?.close();
        }

        document.querySelectorAll('.nav-backdrop').forEach((el) => el.remove());
    }

    public destroy(): void {
        this.backdrop?.hide();
        this.backdrop = null;
        Cart.registry.unregister(this.id);
    }
}